import { Link, useLocation } from '@tanstack/react-router'
import { Menu } from 'antd'
import { type ItemType } from 'antd/es/menu/interface'
import {
  useEffect,
  useMemo,
  type ComponentPropsWithoutRef,
  type FC
} from 'react'
import {
  FaBox,
  FaCalendarCheck,
  FaCog,
  FaCogs,
  FaShippingFast,
  FaTable
} from 'react-icons/fa'
import { FaChalkboardUser, FaRegCalendarCheck } from 'react-icons/fa6'

import {
  LABELS,
  MENU,
  MENU_TOOLTIP
} from '@/constants/common/message.constants'
import { canOpenOwnMasters } from '@/features/auth/models'
// 一時措置 (#4669): パートナー配車板の本開放時に削除する
import { type UserMeExEntity } from '@/libs/api'
import {
  PERFORMANCE_MANAGEMENT_ROLE,
  USER_ROLE,
  USER_TYPE
} from '@/libs/api/types'

import styles from './SideMenu.module.scss'
import { SideMenuSubItem } from './SideMenuSubItem'

/** 業務マスタ設定のうち、パートナーユーザーに出す項目（#6014） */
const PARTNER_MASTER_MENU_KEYS = ['cars', 'drivers']

type Props = Pick<ComponentPropsWithoutRef<typeof Menu>, 'defaultOpenKeys'> & {
  tenantKey: string
  authUser: UserMeExEntity
}

export const SideMenu: FC<Props> = ({
  defaultOpenKeys,
  tenantKey,
  authUser
}) => {
  const location = useLocation()

  // #2719 折りたたみ時のポップアップサブメニューがビューポート外にはみ出す問題の補正
  useEffect(() => {
    const MARGIN = 8
    const styleObservers = new Map<Element, MutationObserver>()

    const adjustPopup = (popupEl: HTMLElement) => {
      const menu = popupEl.querySelector<HTMLElement>('.ant-menu')
      if (!menu) return

      if (getComputedStyle(popupEl).display === 'none') return

      const rect = popupEl.getBoundingClientRect()
      if (rect.height === 0) return

      if (rect.top < MARGIN) {
        // 上にはみ出し → topを補正してビューポート内に収める
        const shift = MARGIN - rect.top
        const currentTop = parseFloat(popupEl.style.top) || 0
        popupEl.style.setProperty('top', `${currentTop + shift}px`, 'important')
        menu.style.setProperty(
          'max-height',
          `${window.innerHeight - MARGIN * 2}px`
        )
        menu.style.overflowY = 'auto'
      } else if (rect.bottom > window.innerHeight - MARGIN) {
        // 下にはみ出し
        menu.style.setProperty(
          'max-height',
          `${window.innerHeight - rect.top - MARGIN}px`
        )
        menu.style.overflowY = 'auto'
      } else {
        // はみ出しなし → 過去の補正スタイルを解除
        menu.style.removeProperty('max-height')
        menu.style.removeProperty('overflow-y')
      }
    }

    const watchPopupStyle = (popupEl: HTMLElement) => {
      if (styleObservers.has(popupEl)) return
      const obs = new MutationObserver(() => adjustPopup(popupEl))
      obs.observe(popupEl, { attributes: true, attributeFilter: ['style'] })
      styleObservers.set(popupEl, obs)
    }

    // body直下のchildList変更のみ監視（ポータルのpopup追加/削除を検知）
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type !== 'childList') continue
        m.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return
          if (node.matches('.ant-menu-submenu-popup')) {
            adjustPopup(node)
            watchPopupStyle(node)
          }
          node
            .querySelectorAll<HTMLElement>('.ant-menu-submenu-popup')
            .forEach((el) => {
              adjustPopup(el)
              watchPopupStyle(el)
            })
        })
        m.removedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return
          const obs = styleObservers.get(node)
          if (obs) {
            obs.disconnect()
            styleObservers.delete(node)
          }
        })
      }
    })
    observer.observe(document.body, { childList: true, subtree: true })
    return () => {
      observer.disconnect()
      styleObservers.forEach((obs) => obs.disconnect())
      styleObservers.clear()
    }
  }, [])

  const menuItems = useMemo(() => {
    // ユーザはSub側かどうか
    const isSubSide = authUser.type === USER_TYPE.PARTNER

    // 実績収支管理権限
    const hasPerformanceManagementPermission =
      authUser.performanceManagementRole ===
        PERFORMANCE_MANAGEMENT_ROLE.ALLOWED ||
      authUser.userRole === USER_ROLE.ADMIN
    // 基本のメニュー項目
    const items: ItemType[] = [
      {
        key: 'dashboard',
        icon: <FaChalkboardUser />,
        label: (
          <Link
            to="/$tenantKey"
            params={{ tenantKey }}
            activeOptions={{ exact: true }}
          >
            {LABELS.DASHBOARD}
          </Link>
        )
      },
      {
        key: 'shipment-orders',
        icon: <FaBox />,
        label: (
          <Link to="/$tenantKey/shipment-orders" params={{ tenantKey }}>
            {MENU.SHIPMENT_ORDER}
          </Link>
        ),
        _exclude: isSubSide
      },
      {
        key: 'dispatch-work',
        icon: <FaTable />,
        label: MENU.DISPATCH_WORK,
        children: [
          {
            key: 'vehicle-dispatch-boards',
            label: (
              <Link
                to="/$tenantKey/vehicle-dispatch-boards"
                params={{ tenantKey }}
              >
                <SideMenuSubItem>{MENU.DISPATCH_BOARD}</SideMenuSubItem>
              </Link>
            )
          },
          {
            key: 'plan-slots',
            label: (
              <Link to="/$tenantKey/settings/plan-slots" params={{ tenantKey }}>
                <SideMenuSubItem>{MENU.PERMANENT_VEHICLES}</SideMenuSubItem>
              </Link>
            ),
            _exclude: isSubSide
          }
        ].filter(excludeMenuItem())
      },
      {
        key: 'tours',
        icon: <FaShippingFast />,
        label: (
          <Link to="/$tenantKey/tours" params={{ tenantKey }}>
            {MENU.TOUR}
          </Link>
        ),
        _exclude: !hasPerformanceManagementPermission
      },
      {
        key: 'monthly-report',
        icon: <FaCalendarCheck />,
        label: (
          <Link to="/$tenantKey/monthly-tour-reports" params={{ tenantKey }}>
            {MENU.MONTHLY_TOUR_REPORT}
          </Link>
        ),
        _exclude: !hasPerformanceManagementPermission
      },
      {
        key: 'monthly-customer-report',
        icon: <FaRegCalendarCheck />,
        label: (
          <Link
            to="/$tenantKey/monthly-customer-reports"
            params={{ tenantKey }}
          >
            {MENU.MONTHLY_CUSTOMER_REPORT}
          </Link>
        ),
        _exclude: isSubSide || !hasPerformanceManagementPermission
      },
      {
        key: 'master',
        icon: <FaCogs />,
        label: MENU.BUSINESS_MASTER_SETTINGS,
        children: [
          {
            key: 'in-house',
            label: (
              <Link
                to="/$tenantKey/settings/own-partner"
                params={{ tenantKey }}
              >
                <SideMenuSubItem>{MENU.OWN_PARTNER_INFO}</SideMenuSubItem>
              </Link>
            ),
            _exclude: !isSubSide
          },
          {
            key: 'partners',
            label: (
              <Link to="/$tenantKey/settings/partners" params={{ tenantKey }}>
                <SideMenuSubItem>{MENU.PARTNER}</SideMenuSubItem>
              </Link>
            ),
            _exclude: isSubSide
          },
          {
            key: 'truck-models',
            label: (
              <Link
                to="/$tenantKey/settings/truck-models"
                params={{ tenantKey }}
              >
                <SideMenuSubItem tooltip={MENU_TOOLTIP.TRUCK_MODEL}>
                  {MENU.TRUCK_MODEL}
                </SideMenuSubItem>
              </Link>
            ),
            _exclude: isSubSide
          },
          {
            key: 'cars',
            label: (
              <Link to="/$tenantKey/settings/trucks" params={{ tenantKey }}>
                <SideMenuSubItem tooltip={MENU_TOOLTIP.TRUCKS}>
                  {MENU.TRUCKS}
                </SideMenuSubItem>
              </Link>
            )
          },
          {
            key: 'drivers',
            label: (
              <Link to="/$tenantKey/settings/drivers" params={{ tenantKey }}>
                <SideMenuSubItem>{MENU.DRIVER}</SideMenuSubItem>
              </Link>
            )
          },
          {
            key: 'sub-partners',
            label: (
              <Link
                to="/$tenantKey/settings/sub-partners"
                params={{ tenantKey }}
              >
                <SideMenuSubItem>{MENU.SUB_PARTNER}</SideMenuSubItem>
              </Link>
            ),
            _exclude: isSubSide
          },
          {
            key: 'deliveryBases',
            label: (
              <Link to="/$tenantKey/settings/sites" params={{ tenantKey }}>
                <SideMenuSubItem>{MENU.SITES}</SideMenuSubItem>
              </Link>
            ),
            _exclude: isSubSide
          },
          {
            key: 'customers',
            label: (
              <Link to="/$tenantKey/settings/customers" params={{ tenantKey }}>
                <SideMenuSubItem>{MENU.CUSTOMER}</SideMenuSubItem>
              </Link>
            ),
            _exclude: isSubSide
          },
          {
            key: 'package-categories',
            label: (
              <Link
                to="/$tenantKey/settings/package-categories"
                params={{ tenantKey }}
              >
                <SideMenuSubItem>{MENU.PACKAGE_CATEGORY}</SideMenuSubItem>
              </Link>
            ),
            _exclude: isSubSide
          },
          {
            key: 'shipment-categories',
            label: (
              <Link
                to="/$tenantKey/settings/shipment-categories"
                params={{ tenantKey }}
              >
                <SideMenuSubItem tooltip={MENU_TOOLTIP.SHIPMENT_CATEGORY}>
                  {MENU.SHIPMENT_CATEGORY}
                </SideMenuSubItem>
              </Link>
            ),
            _exclude: isSubSide
          },
          {
            key: 'contracts',
            label: (
              <Link to="/$tenantKey/settings/contracts" params={{ tenantKey }}>
                <SideMenuSubItem>{MENU.CONTRACT}</SideMenuSubItem>
              </Link>
            )
          },
          {
            key: 'customer-contracts',
            label: (
              <Link
                to="/$tenantKey/settings/customer-contracts"
                params={{ tenantKey }}
              >
                <SideMenuSubItem>{MENU.CUSTOMER_CONTRACT}</SideMenuSubItem>
              </Link>
            ),
            _exclude: isSubSide
          },
          {
            key: 'tariff-import-jobs',
            label: (
              <Link
                to="/$tenantKey/settings/tariff-import-jobs"
                params={{ tenantKey }}
              >
                <SideMenuSubItem>{MENU.TARIFF}</SideMenuSubItem>
              </Link>
            ),
            _exclude: isSubSide
          },
          {
            key: 'custom-key-settings',
            label: (
              <Link
                to="/$tenantKey/settings/custom-key-settings"
                params={{ tenantKey }}
              >
                <SideMenuSubItem>{MENU.CUSTOM_KEY_SETTINGS}</SideMenuSubItem>
              </Link>
            ),
            _exclude: isSubSide
          }
        ]
          .filter(excludeMenuItem())
          // パートナーユーザーに出すのは、自社の車両・ドライバーだけ（#6014）。ほかのマスタは
          // 見せない（#5602）。項目が増えてもパートナーには出ない側に倒すため、出すものを列挙する
          .filter(
            (item) =>
              !isSubSide || PARTNER_MASTER_MENU_KEYS.includes(String(item.key))
          ),
        // ドライバーのログインアカウントにはマスタを見せない。見出しごと外す
        _exclude: isSubSide && !canOpenOwnMasters(authUser)
      },
      {
        key: 'system_master',
        icon: <FaCog />,
        label: MENU.SYSTEM_MASTER_SETTINGS,
        children: [
          {
            key: 'in-house',
            label: (
              <Link to="/$tenantKey/settings/in-house" params={{ tenantKey }}>
                <SideMenuSubItem>{MENU.DC}</SideMenuSubItem>
              </Link>
            ),
            _exclude: isSubSide
          },
          {
            key: 'users',
            label: <SideMenuSubItem>{MENU.USER}</SideMenuSubItem>,
            children: [
              {
                key: 'dc-users',
                label: (
                  <Link
                    to="/$tenantKey/settings/dc-users"
                    params={{ tenantKey }}
                  >
                    <SideMenuSubItem>{MENU.DC_USER}</SideMenuSubItem>
                  </Link>
                )
              },
              {
                key: 'partner-users',
                label: (
                  <Link
                    to="/$tenantKey/settings/partner-users"
                    params={{ tenantKey }}
                  >
                    <SideMenuSubItem>{MENU.PARTNER_USER}</SideMenuSubItem>
                  </Link>
                )
              }
            ],
            _exclude: isSubSide
          },
          {
            key: 'areas',
            label: (
              <Link to="/$tenantKey/settings/areas" params={{ tenantKey }}>
                <SideMenuSubItem>{MENU.AREA}</SideMenuSubItem>
              </Link>
            )
          },
          {
            key: 'truck-classes',
            label: (
              <Link
                to="/$tenantKey/settings/truck-classes"
                params={{ tenantKey }}
              >
                <SideMenuSubItem tooltip={MENU_TOOLTIP.TRUCK_CLASS}>
                  {MENU.TRUCK_CLASS}
                </SideMenuSubItem>
              </Link>
            )
          },
          {
            key: 'truck-types',
            label: (
              <Link
                to="/$tenantKey/settings/truck-types"
                params={{ tenantKey }}
              >
                <SideMenuSubItem tooltip={MENU_TOOLTIP.TRUCK_TYPE}>
                  {MENU.TRUCK_TYPE}
                </SideMenuSubItem>
              </Link>
            )
          },
          {
            key: 'tax-categories',
            label: (
              <Link
                to="/$tenantKey/settings/tax-categories"
                params={{ tenantKey }}
              >
                <SideMenuSubItem>{MENU.TAX_CATEGORIES}</SideMenuSubItem>
              </Link>
            )
          },
          {
            key: 'additional-payment-categories',
            label: (
              <Link
                to="/$tenantKey/settings/additional-payment-categories"
                params={{ tenantKey }}
              >
                <SideMenuSubItem>
                  {MENU.ADDITIONAL_PAYMENT_CATEGORY}
                </SideMenuSubItem>
              </Link>
            )
          },
          {
            key: 'contract-unit-prices',
            label: (
              <Link to="/$tenantKey/settings/item-units" params={{ tenantKey }}>
                <SideMenuSubItem tooltip={MENU_TOOLTIP.TANKA_CONTRACT_UNIT}>
                  {MENU.TANKA_CONTRACT_UNIT}
                </SideMenuSubItem>
              </Link>
            )
          },
          {
            key: 'charge-types',
            label: (
              <Link
                to="/$tenantKey/settings/charge-types"
                params={{ tenantKey }}
              >
                <SideMenuSubItem tooltip={MENU_TOOLTIP.CHARGE_TYPE}>
                  {MENU.CHARGE_TYPE}
                </SideMenuSubItem>
              </Link>
            )
          }
        ].filter(excludeMenuItem()),
        _exclude: isSubSide
      }
    ].filter(excludeMenuItem())

    return items

    // メニュー項目の除外関数
    function excludeMenuItem() {
      return (item: { key: string; _exclude?: boolean }) => {
        const exclude = item._exclude
        delete item._exclude
        return exclude === undefined || exclude !== true
      }
    }
  }, [tenantKey, authUser])

  return (
    <Menu
      mode="inline"
      inlineIndent={16}
      selectable={true}
      defaultOpenKeys={defaultOpenKeys}
      selectedKeys={[location.pathname]}
      className={`${styles.container} font-bold [&_.ant-menu-item:has(a[data-status='active'])]:text-primary`}
      items={menuItems}
    />
  )
}
